import axios from 'axios';
import {
  API_BASE_URL,
  API_PRODUCTS_LIST,
  API_PRODUCTS_DETAILS,
  API_PRODUCTS_CREATE,
  API_PRODUCTS_UPDATE,
  API_PRODUCTS_DELETE,
  API_PRODUCTS_EXPORT_XML,
  API_PRODUCTS_IMPORT_XML,
  API_PRICELISTS_CUSTOMER_LIST,
  API_PRICELISTS_CUSTOMER_DETAILS,
  API_PRICELISTS_CUSTOMER_CREATE,
  API_PRICELISTS_CUSTOMER_UPDATE,
  API_PRICELISTS_CUSTOMER_DELETE,
  API_PRICELISTS_CUSTOMER_APPLICABLE,
  API_PRICELISTS_CUSTOMER_EXPORT_XML,
  API_PRICELISTS_CUSTOMER_IMPORT_XML,
  API_PRICELISTS_SUPPLIER_LIST,
  API_PRICELISTS_SUPPLIER_DETAILS,
  API_PRICELISTS_SUPPLIER_CREATE,
  API_PRICELISTS_SUPPLIER_UPDATE,
  API_PRICELISTS_SUPPLIER_DELETE,
  API_PRICELISTS_SUPPLIER_APPLICABLE,
  API_PRICELISTS_SUPPLIER_EXPORT_XML,
  API_PRICELISTS_SUPPLIER_IMPORT_XML
} from '../../../helpers/urlHelper';

// ========================================================================
// 📦 PRODUCTS SERVICE
// ========================================================================

/**
 * Obtener listado de productos con filtros
 * @param {Object} filters - { type, serviceType, category, isActive, search }
 */
export const getProducts = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.type) params.append('type', filters.type);
    if (filters.serviceType) params.append('serviceType', filters.serviceType);
    if (filters.category) params.append('category', filters.category);
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
    if (filters.search) params.append('search', filters.search);
    
    const url = `${API_BASE_URL}${API_PRODUCTS_LIST}?${params.toString()}`;
    console.log('🔍 Fetching products from:', url);
    
    const response = await axios.get(url);
    console.log('✅ API Response:', response);
    console.log('📦 Response data:', response.data);
    
    // AQUÍ ESTÁ EL CAMBIO IMPORTANTE:
    // Verificar si la respuesta tiene estructura { data: [...] } o es directamente un array
    let products = [];
    
    if (Array.isArray(response.data)) {
      // Si response.data es directamente un array
      products = response.data;
    } else if (response.data && Array.isArray(response.data.data)) {
      // Si response.data tiene estructura { data: [...] }
      products = response.data.data;
    } else if (response.data && response.data.products) {
      // Si response.data tiene estructura { products: [...] }
      products = response.data.products;
    }
    
    console.log('📋 Products extracted:', products);
    return products;
    
  } catch (error) {
    console.error('❌ Error fetching products:', error);
    console.error('Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error; // Re-lanzar el error para manejarlo en el componente
  }
};

/**
 * Obtener detalle de un producto por ID
 * @param {string} id - ID del producto
 */
export const getProductById = async (id) => {
  const response = await axios.get(`${API_BASE_URL}${API_PRODUCTS_DETAILS}/${id}`);
  return response.data;
};

/**
 * Crear nuevo producto
 * @param {Object} productData - Datos del producto
 */
export const createProduct = async (productData) => {
  const response = await axios.post(`${API_BASE_URL}${API_PRODUCTS_CREATE}`, productData);
  return response.data;
};

/**
 * Actualizar producto existente
 * @param {string} id - ID del producto
 * @param {Object} productData - Datos a actualizar
 */
export const updateProduct = async (id, productData) => {
  const response = await axios.put(`${API_BASE_URL}${API_PRODUCTS_UPDATE}/${id}`, productData);
  return response.data;
};

/**
 * Eliminar producto (soft delete)
 * @param {string} id - ID del producto
 */
export const deleteProduct = async (id) => {
  const response = await axios.delete(`${API_BASE_URL}${API_PRODUCTS_DELETE}/${id}`);
  return response.data;
};

/**
 * Exportar productos a XML
 * @param {Object} filters - { type, category, isActive }
 */
export const exportProductsToXML = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.type) params.append('type', filters.type);
  if (filters.category) params.append('category', filters.category);
  if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
  
  const response = await axios.get(`${API_BASE_URL}${API_PRODUCTS_EXPORT_XML}?${params.toString()}`, {
    responseType: 'blob'
  });
  
  // Crear enlace de descarga
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `products_${Date.now()}.xml`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  
  return response.data;
};

/**
 * Importar productos desde XML
 * @param {File} file - Archivo XML
 */
export const importProductsFromXML = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(`${API_BASE_URL}${API_PRODUCTS_IMPORT_XML}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

// ========================================================================
// 💰 CUSTOMER PRICELISTS SERVICE
// ========================================================================

/**
 * Obtener listado de tarifas de clientes con filtros
 * @param {Object} filters - { productid, userid, companyid, active }
 */
export const getPricelistsCustomer = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.productid) params.append('productid', filters.productid);
  if (filters.userid) params.append('userid', filters.userid);
  if (filters.companyid) params.append('companyid', filters.companyid);
  if (filters.active !== undefined) params.append('active', filters.active);
  
  const response = await axios.get(`${API_BASE_URL}${API_PRICELISTS_CUSTOMER_LIST}?${params.toString()}`);
  return response.data;
};

/**
 * Obtener detalle de una tarifa de cliente por ID
 * @param {string} id - ID de la tarifa
 */
export const getPricelistCustomerById = async (id) => {
  const response = await axios.get(`${API_BASE_URL}${API_PRICELISTS_CUSTOMER_DETAILS}/${id}`);
  return response.data;
};

/**
 * Crear nueva tarifa de cliente
 * @param {Object} pricelistData - Datos de la tarifa
 */
export const createPricelistCustomer = async (pricelistData) => {
  const response = await axios.post(`${API_BASE_URL}${API_PRICELISTS_CUSTOMER_CREATE}`, pricelistData);
  return response.data;
};

/**
 * Actualizar tarifa de cliente existente
 * @param {string} id - ID de la tarifa
 * @param {Object} pricelistData - Datos a actualizar
 */
export const updatePricelistCustomer = async (id, pricelistData) => {
  const response = await axios.put(`${API_BASE_URL}${API_PRICELISTS_CUSTOMER_UPDATE}/${id}`, pricelistData);
  return response.data;
};

/**
 * Eliminar tarifa de cliente
 * @param {string} id - ID de la tarifa
 */
export const deletePricelistCustomer = async (id) => {
  const response = await axios.delete(`${API_BASE_URL}${API_PRICELISTS_CUSTOMER_DELETE}/${id}`);
  return response.data;
};

/**
 * Obtener precio aplicable para un cliente específico
 * @param {Object} params - { productid, userid, companyid, quantity }
 */
export const getApplicableCustomerPrice = async (params) => {
  const queryParams = new URLSearchParams();
  if (params.productid) queryParams.append('productid', params.productid);
  if (params.userid) queryParams.append('userid', params.userid);
  if (params.companyid) queryParams.append('companyid', params.companyid);
  if (params.quantity) queryParams.append('quantity', params.quantity);
  
  const response = await axios.get(`${API_BASE_URL}${API_PRICELISTS_CUSTOMER_APPLICABLE}?${queryParams.toString()}`);
  return response.data;
};

/**
 * Exportar tarifas de clientes a XML
 * @param {Object} filters - { productid, userid, companyid, active }
 */
export const exportPricelistsCustomerToXML = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.productid) params.append('productid', filters.productid);
  if (filters.userid) params.append('userid', filters.userid);
  if (filters.companyid) params.append('companyid', filters.companyid);
  if (filters.active !== undefined) params.append('active', filters.active);
  
  const response = await axios.get(`${API_BASE_URL}${API_PRICELISTS_CUSTOMER_EXPORT_XML}?${params.toString()}`, {
    responseType: 'blob'
  });
  
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `pricelists_customer_${Date.now()}.xml`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  
  return response.data;
};

/**
 * Importar tarifas de clientes desde XML
 * @param {File} file - Archivo XML
 */
export const importPricelistsCustomerFromXML = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(`${API_BASE_URL}${API_PRICELISTS_CUSTOMER_IMPORT_XML}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

// ========================================================================
// 🏭 SUPPLIER PRICELISTS SERVICE
// ========================================================================

/**
 * Obtener listado de tarifas de proveedores con filtros
 * @param {Object} filters - { productid, supplierid, userid, active }
 */
export const getPricelistsSupplier = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.productid) params.append('productid', filters.productid);
  if (filters.supplierid) params.append('supplierid', filters.supplierid);
  if (filters.userid) params.append('userid', filters.userid);
  if (filters.active !== undefined) params.append('active', filters.active);
  
  const response = await axios.get(`${API_BASE_URL}${API_PRICELISTS_SUPPLIER_LIST}?${params.toString()}`);
  return response.data;
};

/**
 * Obtener detalle de una tarifa de proveedor por ID
 * @param {string} id - ID de la tarifa
 */
export const getPricelistSupplierById = async (id) => {
  const response = await axios.get(`${API_BASE_URL}${API_PRICELISTS_SUPPLIER_DETAILS}/${id}`);
  return response.data;
};

/**
 * Crear nueva tarifa de proveedor
 * @param {Object} pricelistData - Datos de la tarifa
 */
export const createPricelistSupplier = async (pricelistData) => {
  const response = await axios.post(`${API_BASE_URL}${API_PRICELISTS_SUPPLIER_CREATE}`, pricelistData);
  return response.data;
};

/**
 * Actualizar tarifa de proveedor existente
 * @param {string} id - ID de la tarifa
 * @param {Object} pricelistData - Datos a actualizar
 */
export const updatePricelistSupplier = async (id, pricelistData) => {
  const response = await axios.put(`${API_BASE_URL}${API_PRICELISTS_SUPPLIER_UPDATE}/${id}`, pricelistData);
  return response.data;
};

/**
 * Eliminar tarifa de proveedor
 * @param {string} id - ID de la tarifa
 */
export const deletePricelistSupplier = async (id) => {
  const response = await axios.delete(`${API_BASE_URL}${API_PRICELISTS_SUPPLIER_DELETE}/${id}`);
  return response.data;
};

/**
 * Obtener precio aplicable para un proveedor específico
 * @param {Object} params - { productid, supplierid, userid, quantity }
 */
export const getApplicableSupplierPrice = async (params) => {
  const queryParams = new URLSearchParams();
  if (params.productid) queryParams.append('productid', params.productid);
  if (params.supplierid) queryParams.append('supplierid', params.supplierid);
  if (params.userid) queryParams.append('userid', params.userid);
  if (params.quantity) queryParams.append('quantity', params.quantity);
  
  const response = await axios.get(`${API_BASE_URL}${API_PRICELISTS_SUPPLIER_APPLICABLE}?${queryParams.toString()}`);
  return response.data;
};

/**
 * Exportar tarifas de proveedores a XML
 * @param {Object} filters - { productid, supplierid, userid, active }
 */
export const exportPricelistsSupplierToXML = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.productid) params.append('productid', filters.productid);
  if (filters.supplierid) params.append('supplierid', filters.supplierid);
  if (filters.userid) params.append('userid', filters.userid);
  if (filters.active !== undefined) params.append('active', filters.active);
  
  const response = await axios.get(`${API_BASE_URL}${API_PRICELISTS_SUPPLIER_EXPORT_XML}?${params.toString()}`, {
    responseType: 'blob'
  });
  
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `pricelists_supplier_${Date.now()}.xml`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  
  return response.data;
};

/**
 * Importar tarifas de proveedores desde XML
 * @param {File} file - Archivo XML
 */
export const importPricelistsSupplierFromXML = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(`${API_BASE_URL}${API_PRICELISTS_SUPPLIER_IMPORT_XML}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

